// StageDocumentsCard — per-stage document generation with review workflow.
//
// Introduced 2026-05-20 as slice 2 of the "integrate document creation
// into the case stage process" feature. Sits on the Overview tab below
// the StageTracker.
//
// Two sections:
//   1. Documents for [Current Stage] — templates tagged with the
//      current stage. Click Generate → creates a draft (calls the new
//      POST /api/matters/{id}/drafts endpoint). Nothing hits Clio yet.
//   2. Drafts — pending and recently-actioned. Preview opens the .docx
//      rendered as PDF in a new tab (uses /api/drafts/{id}/preview.pdf).
//      Approve uploads to Clio; Reject discards.
//
// Approval is auth-gated server-side to the matter's responsible
// attorney (or SuperAdmin/Partner). Client-side just calls the
// endpoint and surfaces the 403 as a notification.
//
// Slice 3 will layer on: an inline preview modal instead of the
// new-tab PDF link, fix-merge-fields form for edit-before-approve,
// and download-edit-Word-reupload flow. This slice keeps the modal
// out to stay focused.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch, cachedFetch, fetchMatterProviders, fetchStageDashboard } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { Badge } from "../components/Badge.jsx";

const STATUS_STYLES = {
  draft:    { label: "Pending review", color: C.gold,    bg: `${C.gold}18` },
  approved: { label: "Approved",        color: C.success, bg: C.successBg },
  rejected: { label: "Rejected",        color: C.error,   bg: C.errorBg },
};

// `headerAction` renders in the top-right of the card header — the
// Documents tab passes the Generate Package button there so bulk and
// one-at-a-time generation sit on the same card instead of two cards
// each restating the current stage.
export function StageDocumentsCard({ matterId, showNotification, showDrafts = true, headerAction = null, onOpenAdHoc = null }) {
  // Substep 5-ν.3: send-an-approved-draft state. Recipients come from
  // the universal directory, so a delivery failure feeds that
  // contact's reliability for every firm.
  const [sendingDraft, setSendingDraft] = useState(null);     // draft being sent
  const [sendRecipients, setSendRecipients] = useState([]);   // directory matches
  const [sendChoice, setSendChoice] = useState(null);         // chosen recipient
  const [sendChannel, setSendChannel] = useState("fax");
  const [sendSearch, setSendSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [sendHistory, setSendHistory] = useState([]);
  // openSend is defined above the providers state below, so it reads
  // the loaded roster through a ref rather than re-fetching it.
  const providersRef = React.useRef([]);

  const loadSendHistory = React.useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/dispatches`);
      setSendHistory(d.data || []);
    } catch { /* history is informational; failure shouldn't block */ }
  }, [matterId]);

  const openSend = async (draft) => {
    setSendingDraft(draft);
    setSendChoice(null);
    setSendChannel("fax");
    setSendSearch("");
    try {
      // Seed the picker with providers already on this matter — the
      // overwhelmingly common case is sending to one of them. loadAll
      // already has them; refetching here was a second round trip for
      // data sitting in state. The directory itself barely changes
      // within a session, so it rides the TTL cache.
      const names = providersRef.current.map(p => p.name).filter(Boolean);
      const dir = await cachedFetch(
        `/api/directory?directory_type=medical_provider&limit=400`, 120_000);
      const all = dir.data || [];
      const onMatter = all.filter(e =>
        names.some(n => (e.name || "").toLowerCase().includes(n.toLowerCase())
                     || n.toLowerCase().includes((e.name || "").toLowerCase())));
      setSendRecipients(onMatter.length ? onMatter : all.slice(0, 60));
    } catch (e) {
      showNotification(`Couldn't load recipients: ${e.message}`, "error");
      setSendRecipients([]);
    }
  };

  const doSend = async () => {
    if (!sendingDraft || !sendChoice) return;
    setSending(true);
    try {
      const r = await apiFetch(`/api/matters/${matterId}/send-document`, {
        method: "POST",
        body: JSON.stringify({
          draft_id: sendingDraft.id,
          channel: sendChannel,
          directory_entry_id: sendChoice.id,
        }),
      });
      if (r.status === "delivered") {
        showNotification(`Delivered to ${sendChoice.name}.`, "success");
      } else if (r.status === "queued") {
        showNotification(
          `${sendChoice.name} line was ${r.failure_kind}. Queued — it will retry automatically.`,
          "warning");
      } else {
        showNotification(
          `Send failed: ${r.error || r.failure_kind}. This contact has been flagged for review.`,
          "error");
      }
      setSendingDraft(null);
      await loadSendHistory();
    } catch (e) {
      showNotification(`Send failed: ${e.message}`, "error");
    }
    setSending(false);
  };

  useEffect(() => { loadSendHistory(); }, [loadSendHistory]);

  // Substep 5-η.3: showDrafts=false hides the Drafts subsection —
  // used on the Overview tab where drafts were adding ~300px of
  // clutter to a page that already had 4 substantial cards. Full
  // draft review still available on the Documents tab (showDrafts
  // defaults to true so existing call sites keep behavior).
  const [stageInfo, setStageInfo] = useState(null);   // {key, label, color}
  const [templates, setTemplates] = useState([]);     // templates for current stage
  const [drafts, setDrafts] = useState([]);           // all drafts for this matter
  const [providers, setProviders] = useState([]);     // matter's providers, for records-request templates
  const [providerSelections, setProviderSelections] = useState({}); // {template_filename: provider_name}
  const [loading, setLoading] = useState(true);
  const [generatingFilename, setGeneratingFilename] = useState(null);
  const [actioningDraftId, setActioningDraftId] = useState(null);
  // When Generate returns 422 (missing fields), we surface an inline
  // form under the offending template row instead of just a warning.
  // Only one form open at a time (you're generating one template at
  // a time). null when no form is open.
  //
  // Shape: { templateFilename, fields: [...], overrides: {}, writeBack: bool }
  const [pendingMissing, setPendingMissing] = useState(null);
  // Draft currently open in the preview modal. null when no modal.
  const [previewingDraft, setPreviewingDraft] = useState(null);
  // Which template row the pointer is over. Drives the action label:
  // at rest each row shows only a small bolt, so the word "Generate"
  // appears once (on the row you are pointing at) instead of once per
  // template down the whole list.
  const [hoverRow, setHoverRow] = useState(null);
  // Substep 5-π.5: the template list is collapsed on load. A stage can
  // carry a dozen templates, and most visits to this tab are about the
  // drafts already in flight, not about starting a new one — an open
  // list pushed the drafts below the fold every time.
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Get current stage
      const dash = await fetchStageDashboard(matterId);
      const stage = {
        key: dash.current_stage,
        label: dash.current_stage_label,
        color: dash.current_stage_info?.color || C.gold,
      };
      setStageInfo(stage);

      // 2. Fetch templates + drafts + providers in parallel
      const [tplResp, draftResp, provResp] = await Promise.all([
        apiFetch(`/api/templates?stage=${encodeURIComponent(stage.label)}`),
        apiFetch(`/api/matters/${matterId}/drafts`),
        fetchMatterProviders(matterId),
      ]);
      setTemplates(tplResp.data || []);
      setDrafts(draftResp.data || []);
      setProviders(provResp.data || []);
      providersRef.current = provResp.data || [];
    } catch (e) {
      showNotification("Failed to load stage documents: " + e.message, "error");
    }
    setLoading(false);
  }, [matterId, showNotification]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // handleGenerate is called from the row's Generate button AND from
  // the "Retry Generate" button inside the missing-fields inline form.
  // The overrides argument lets the retry path pass the values the
  // attorney filled in; writeBack controls whether those values also
  // persist to Clio's matter custom fields.
  const handleGenerate = async (template, overrides = null, writeBack = true) => {
    if (generatingFilename) return;
    // Guard: templates that require a provider need one selected
    const providerName = providerSelections[template.filename];
    if (template.requires_provider && !providerName) {
      showNotification(
        `${template.name || template.filename}: pick a provider before generating.`,
        "warning",
      );
      return;
    }
    setGeneratingFilename(template.filename);
    try {
      const resp = await apiFetch(`/api/matters/${matterId}/drafts`, {
        method: "POST",
        body: JSON.stringify({
          template_filename: template.filename,
          provider_name: providerName || null,
          stage_key: stageInfo?.key,
          field_overrides: overrides || null,
          write_back_to_clio: writeBack,
        }),
      });
      const newDraft = resp.data;
      setDrafts(prev => [newDraft, ...prev]);
      // Clear the provider selection + any open missing-fields form
      // for this template so the next Generate starts fresh
      setProviderSelections(prev => {
        const next = { ...prev };
        delete next[template.filename];
        return next;
      });
      if (pendingMissing?.templateFilename === template.filename) {
        setPendingMissing(null);
      }
      const msg = overrides
        ? `Draft created (${Object.keys(overrides).length} field${Object.keys(overrides).length === 1 ? "" : "s"} filled in). Awaiting review.`
        : `Draft created: ${template.name || template.filename}. Awaiting review.`;
      showNotification(msg, "success");
    } catch (e) {
      // 422 = missing required fields. Open the inline form so
      // attorney can fill them in and retry. apiFetch attaches the
      // full response body to the error (see api.js) so we can read
      // e.body.missing directly.
      if (e.status === 422 && e.body?.missing) {
        // Prefill overrides with any existing values from the
        // previous attempt so a retry-after-partial-fill doesn't
        // lose typed data
        const existingOverrides = pendingMissing?.templateFilename === template.filename
          ? pendingMissing.overrides
          : {};
        setPendingMissing({
          templateFilename: template.filename,
          fields: e.body.missing,
          overrides: existingOverrides,
          writeBack: pendingMissing?.writeBack ?? true,
        });
        // Also show a notification so it's obvious something happened
        // even if the form is below the fold
        const missingLabels = e.body.missing
          .map(f => f.label || f.field)
          .filter(Boolean)
          .slice(0, 3)
          .join(", ");
        showNotification(
          `${template.name || template.filename}: missing ${missingLabels}. Fill in below and retry.`,
          "warning",
        );
      } else {
        showNotification(`Generate failed: ${e.message}`, "error");
      }
    }
    setGeneratingFilename(null);
  };

  const setProviderForTemplate = (filename, providerName) => {
    setProviderSelections(prev => ({ ...prev, [filename]: providerName }));
  };

  // Missing-fields form state helpers
  const updateMissingOverride = (fieldName, value) => {
    setPendingMissing(prev => prev && ({
      ...prev,
      overrides: { ...prev.overrides, [fieldName]: value },
    }));
  };

  const toggleWriteBack = () => {
    setPendingMissing(prev => prev && ({ ...prev, writeBack: !prev.writeBack }));
  };

  const cancelMissingFieldsForm = () => setPendingMissing(null);

  const retryWithOverrides = (template) => {
    if (!pendingMissing || pendingMissing.templateFilename !== template.filename) return;
    handleGenerate(template, pendingMissing.overrides, pendingMissing.writeBack);
  };

  const handleApprove = async (draft) => {
    if (actioningDraftId) return;
    setActioningDraftId(draft.id);
    try {
      const resp = await apiFetch(`/api/drafts/${draft.id}/approve`, {
        method: "POST",
      });
      setDrafts(prev => prev.map(d => d.id === draft.id ? resp.data : d));
      showNotification(`Approved and uploaded to Clio.`, "success");
    } catch (e) {
      // 403 comes back when the current user isn't the responsible
      // attorney (or SuperAdmin/Partner). Server message is descriptive.
      showNotification(`Approve failed: ${e.message}`, "error");
    }
    setActioningDraftId(null);
  };

  const handleReject = async (draft) => {
    if (actioningDraftId) return;
    const reason = window.prompt(
      `Reject this draft?\n\n(Optional: enter a reason for the log)`,
      "",
    );
    if (reason === null) return; // user cancelled
    setActioningDraftId(draft.id);
    try {
      const resp = await apiFetch(`/api/drafts/${draft.id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || null }),
      });
      setDrafts(prev => prev.map(d => d.id === draft.id ? resp.data : d));
      showNotification("Draft rejected.", "success");
    } catch (e) {
      showNotification(`Reject failed: ${e.message}`, "error");
    }
    setActioningDraftId(null);
  };

  // Word-edit-reupload: attorney downloads the .docx, edits in Word,
  // uploads the edited version back. The uploaded bytes replace the
  // draft's file_bytes; the draft row surfaces "Manually edited" so
  // the approver knows they're looking at the attorney's version.
  const handleUploadReplacement = async (draft, file) => {
    if (actioningDraftId) return;
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      showNotification("Only .docx files can replace a draft. Save-as .docx from Word first.", "warning");
      return;
    }
    setActioningDraftId(draft.id);
    try {
      // fetch directly (not apiFetch) — this is multipart/form-data,
      // not JSON. apiFetch defaults to Content-Type application/json
      // which breaks the upload.
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/drafts/${draft.id}/replace`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || err.message || `Upload failed (${res.status})`);
      }
      const resp = await res.json();
      setDrafts(prev => prev.map(d => d.id === draft.id ? resp.data : d));
      showNotification(`Draft updated with your edited version.`, "success");
    } catch (e) {
      showNotification(`Upload failed: ${e.message}`, "error");
    }
    setActioningDraftId(null);
  };

  if (loading && !stageInfo) {
    return React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" },
    },
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } }, "Loading stage documents..."),
      ),
    );
  }

  // Drafts currently pending review, most recent first (list is
  // already server-sorted desc by generated_at)
  const pendingDrafts = drafts.filter(d => d.status === "draft");
  const recentActioned = drafts.filter(d => d.status !== "draft").slice(0, 5);

  // Forced open while a missing-fields form is pending — that form
  // renders inside a template row, so collapsing over it would hide
  // the thing the attorney was just asked to fill in.
  const listOpen = templatesOpen || !!pendingMissing;

  return React.createElement(React.Fragment, null,
  React.createElement("div", {
    style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" },
  },
    // Header
    React.createElement("div", {
      style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" },
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, margin:0 },
        }, "Documents for this stage"),
        stageInfo && React.createElement("div", {
          style: { fontSize:"13px", color:C.navy, fontWeight:500, marginTop:"4px" },
        },
          React.createElement("span", {
            style: { color: stageInfo.color || C.gold, fontWeight:700 },
          }, stageInfo.label),
          React.createElement("span", { style: { color:C.textMuted, fontWeight:400 } },
            ` · ${templates.length} template${templates.length === 1 ? "" : "s"}`),
        ),
      ),
      headerAction,
    ),

    // Templates list.
    //
    // Substep 5-π.4: every row used to carry a filled gold "Generate"
    // pill and, for provider templates, a full-width provider dropdown
    // underneath. Ten templates read as ten gold blocks stacked down
    // the right edge with selects between them — the repetition made
    // every row look equally like the one to click, which is the
    // opposite of what a list is for.
    //
    // Now it is a plain divider list: names on the left (that is what
    // you scan), the provider select inline and compact where it is
    // needed, and the action as a quiet gold link that fills in on
    // hover. Rows that already produced a draft are tinted and their
    // action reads "Generate again", so the eye skips what is done.
    templates.length === 0
      ? React.createElement("div", { style: { fontSize:"13px", color:C.textMuted, padding:"12px 0" } },
          "No templates configured for this stage.")
      : React.createElement("div", { style: { marginBottom: "20px" } },
        React.createElement("button", {
          onClick: () => setTemplatesOpen(o => !o),
          "aria-expanded": listOpen,
          style: {
            width:"100%", display:"flex", alignItems:"center", gap:"8px",
            padding:"9px 12px",
            borderRadius: listOpen ? "8px 8px 0 0" : "8px",
            border:`1px solid ${C.warmGray}60`,
            borderBottom: listOpen ? "none" : `1px solid ${C.warmGray}60`,
            background: C.warmWhite, cursor:"pointer", textAlign:"left",
            fontSize:"12px", fontWeight:600, color:C.navy,
          },
        },
          React.createElement("span", {
            style: {
              display:"inline-flex", color:C.textMuted, flexShrink:0,
              transform: listOpen ? "rotate(90deg)" : "none",
              transition:"transform 0.15s",
            },
          }, I.chevron),
          "Generate a single document",
          React.createElement("span", { style: { color:C.textMuted, fontWeight:400 } },
            ` — ${templates.length} template${templates.length === 1 ? "" : "s"} for this stage`),
        ),

        listOpen && React.createElement("div", {
          style: {
            border: `1px solid ${C.warmGray}60`, borderTop: "none",
            borderRadius: "0 0 8px 8px", overflow: "hidden",
          },
        },
          templates.map((tpl, idx) => {
            const isGenerating = generatingFilename === tpl.filename;
            const hasPending = pendingDrafts.some(d => d.template_filename === tpl.filename);
            const needsProvider = !!tpl.requires_provider;
            const selectedProvider = providerSelections[tpl.filename] || "";
            const canGenerate = !needsProvider || !!selectedProvider;
            const noProviders = needsProvider && providers.length === 0;

            const showLabel = hoverRow === tpl.filename;

            return React.createElement("div", {
              key: tpl.filename,
              onMouseEnter: () => setHoverRow(tpl.filename),
              onMouseLeave: () => setHoverRow(h => (h === tpl.filename ? null : h)),
              style: {
                padding:"10px 12px",
                borderTop: idx === 0 ? "none" : `1px solid ${C.warmGray}40`,
                background: showLabel ? C.warmWhite : hasPending ? `${C.gold}06` : "#fff",
                transition:"background 0.12s",
              },
            },
              React.createElement("div", {
                style: { display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" },
              },
                React.createElement("div", { style: { flex:"1 1 200px", minWidth: 0 } },
                  React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
                    tpl.name || tpl.filename,
                  ),
                  React.createElement("div", {
                    style: { fontSize:"11px", color:C.textMuted, marginTop:"2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
                  },
                    tpl.filename,
                    noProviders && React.createElement("span", {
                      style: { color:C.textLight, marginLeft:"6px", fontStyle:"italic" },
                    }, "· add a provider on the Providers tab first"),
                  ),
                ),

                hasPending && React.createElement("span", {
                  style: {
                    padding:"3px 9px", borderRadius:"9999px", background:`${C.gold}18`,
                    color:C.gold, fontSize:"10px", fontWeight:700, letterSpacing:"0.03em",
                    flexShrink:0, textTransform:"uppercase",
                  },
                }, "Draft pending"),

                // Provider select — inline and only where it applies, so
                // it stops adding a full-width row per template.
                needsProvider && !noProviders && React.createElement("select", {
                  value: selectedProvider,
                  onChange: e => setProviderForTemplate(tpl.filename, e.target.value),
                  disabled: !!generatingFilename,
                  style: {
                    width:"200px", maxWidth:"100%", padding:"5px 8px", borderRadius:"6px",
                    border:`1px solid ${selectedProvider ? C.warmGray : C.gold}`,
                    background:"#fff", fontSize:"12px", color:C.textDark,
                    cursor:"pointer", flexShrink:0,
                  },
                },
                  React.createElement("option", { value:"" }, "Select a provider…"),
                  providers.map((p, i) => React.createElement("option", {
                    key: p.id || i,
                    value: p.name,
                  }, p.name + (p.category ? ` (${p.category})` : ""))),
                ),

                // Bolt only at rest; the word appears on the row under
                // the pointer (or the focused row) and while running.
                React.createElement("button", {
                  onClick: () => handleGenerate(tpl),
                  disabled: !!generatingFilename || !canGenerate,
                  "aria-label": hasPending ? `Generate ${tpl.name || tpl.filename} again` : `Generate ${tpl.name || tpl.filename}`,
                  title: noProviders
                    ? "Add providers to this matter first (Providers tab)"
                    : (needsProvider && !selectedProvider
                        ? "Pick a provider first"
                        : (hasPending ? "Generate another draft" : "Generate a draft")),
                  onFocus: () => setHoverRow(tpl.filename),
                  onBlur: () => setHoverRow(h => (h === tpl.filename ? null : h)),
                  style: {
                    padding:"5px 10px", borderRadius:"6px", minWidth:"34px",
                    border:`1px solid ${showLabel && canGenerate ? `${C.gold}50` : "transparent"}`,
                    background: showLabel && canGenerate ? `${C.gold}12` : "transparent",
                    color: canGenerate ? C.gold : C.textLight,
                    fontSize:"12px", fontWeight:700,
                    cursor: canGenerate ? (generatingFilename ? "wait" : "pointer") : "not-allowed",
                    flexShrink: 0, whiteSpace:"nowrap",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:"6px",
                    transition:"background 0.15s, border-color 0.15s",
                  },
                }, I.zap,
                  (isGenerating || showLabel) && React.createElement("span", null,
                    isGenerating ? "Generating…" : hasPending ? "Generate again" : "Generate"),
                ),
              ),

              // Row 3: inline missing-fields form. Only when THIS template
              // has a pending missing-fields state (Generate returned 422).
              // Attorney fills the fields, hits Retry Generate → same
              // endpoint retries with field_overrides. If write_back is on,
              // writable custom-field overrides also persist to Clio so
              // the next Generate for any template using this field
              // doesn't re-prompt.
              pendingMissing?.templateFilename === tpl.filename && React.createElement("div", {
                style: {
                  marginTop:"4px", padding:"12px 14px",
                  borderRadius:"8px", background:`${C.gold}10`,
                  border:`1px solid ${C.gold}40`,
                },
              },
                React.createElement("div", {
                  style: { fontSize:"12px", fontWeight:600, color:C.navy, marginBottom:"10px" },
                }, `Missing ${pendingMissing.fields.length} required field${pendingMissing.fields.length === 1 ? "" : "s"} — fill in and retry:`),

                // One input per missing field. Date-shaped fields get a
                // date picker; everything else gets a text input.
                pendingMissing.fields.map(f => {
                  const writable = f.source === "custom_field";
                  return React.createElement("div", { key: f.field, style: { marginBottom:"10px" } },
                    React.createElement("label", {
                      style: { fontSize:"11px", color:C.textMuted, fontWeight:600, display:"block", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.03em" },
                    }, f.label || f.field),
                    React.createElement("input", {
                      type: f.field.includes("date") ? "date" : "text",
                      value: pendingMissing.overrides[f.field] || "",
                      onChange: e => updateMissingOverride(f.field, e.target.value),
                      placeholder: f.label || f.field,
                      disabled: !!generatingFilename,
                      style: {
                        width:"100%", padding:"6px 10px", borderRadius:"6px",
                        border:`1px solid ${C.warmGray}`, fontSize:"13px",
                        color:C.textDark, background:"#fff",
                      },
                    }),
                    React.createElement("div", {
                      style: { fontSize:"10px", color:C.textLight, marginTop:"2px" },
                    },
                      "Source: ", f.source || "unknown",
                      writable && pendingMissing.writeBack && React.createElement("span", {
                        style: { color:C.gold, fontWeight:500, marginLeft:"6px" },
                      }, "→ will save to Clio"),
                    ),
                  );
                }),

                // Write-back-to-Clio toggle. Default ON so the attorney
                // filling in "Known Injuries" once persists to Clio and
                // doesn't get re-prompted on the next template using
                // that field.
                React.createElement("label", {
                  style: {
                    display:"flex", alignItems:"center", gap:"8px",
                    padding:"8px 10px", borderRadius:"6px",
                    background: pendingMissing.writeBack ? `${C.gold}08` : "transparent",
                    cursor:"pointer", marginBottom:"10px",
                    fontSize:"12px", color:C.textDark,
                  },
                },
                  React.createElement("input", {
                    type: "checkbox",
                    checked: pendingMissing.writeBack,
                    onChange: toggleWriteBack,
                    disabled: !!generatingFilename,
                    style: { cursor:"pointer" },
                  }),
                  React.createElement("span", null,
                    "Save these values to Clio for future documents",
                  ),
                ),

                // Retry + Cancel buttons
                React.createElement("div", { style: { display:"flex", gap:"8px" } },
                  React.createElement("button", {
                    onClick: () => retryWithOverrides(tpl),
                    disabled: !!generatingFilename,
                    style: {
                      padding:"6px 14px", borderRadius:"6px",
                      border:`1px solid ${C.gold}`,
                      background: generatingFilename ? C.warmWhite : C.gold,
                      color: generatingFilename ? C.textMuted : "#fff",
                      fontSize:"12px", fontWeight:600,
                      cursor: generatingFilename ? "wait" : "pointer",
                    },
                  }, isGenerating ? "Generating…" : "Retry Generate"),
                  React.createElement("button", {
                    onClick: cancelMissingFieldsForm,
                    disabled: !!generatingFilename,
                    style: {
                      padding:"6px 12px", borderRadius:"6px",
                      border:`1px solid ${C.warmGray}`, background:"#fff",
                      color:C.textMuted, fontSize:"12px", fontWeight:500,
                      cursor: generatingFilename ? "wait" : "pointer",
                    },
                  }, "Cancel"),
                ),
              ),
            );
          }),
        ),

        // The escape hatch, at the foot of the list rather than in a
        // card of its own: someone who just scanned these templates and
        // did not find theirs is already looking here.
        listOpen && onOpenAdHoc && React.createElement("div", {
          style: { marginTop:"8px", fontSize:"12px", color:C.textMuted },
        },
          "Not one of these? ",
          React.createElement("button", {
            onClick: onOpenAdHoc,
            style: {
              border:"none", background:"none", padding:0, cursor:"pointer",
              fontSize:"12px", fontWeight:600, color:C.navy,
              textDecoration:"underline", textUnderlineOffset:"3px",
            },
          }, "generate any template for this matter"),
        ),
      ),

    // Drafts section (pending review + recent history) —
    // Substep 5-η.3: gated on showDrafts so Overview can suppress it.
    showDrafts && (pendingDrafts.length > 0 || recentActioned.length > 0) && React.createElement("div", {
      style: { borderTop: `1px solid ${C.warmGray}40`, paddingTop:"16px" },
    },
      React.createElement("h3", {
        style: { fontSize:"12px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600, marginTop:0, marginBottom:"10px" },
      }, `Drafts (${pendingDrafts.length} pending)`),

      React.createElement("div", { style: { display:"flex", flexDirection:"column", gap:"8px" } },
        [...pendingDrafts, ...recentActioned].map(d => {
          const sty = STATUS_STYLES[d.status] || STATUS_STYLES.draft;
          const canAction = d.status === "draft";
          const isActioning = actioningDraftId === d.id;
          return React.createElement("div", {
            key: d.id,
            style: {
              padding:"12px 14px", borderRadius:"8px",
              background: "#fff", border:`1px solid ${C.warmGray}`,
            },
          },
            // Row 1: name + status pill
            React.createElement("div", {
              style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" },
            },
              React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
                d.template_filename,
                d.provider_name && React.createElement("span", {
                  style: { color:C.textMuted, fontWeight:400 },
                }, ` · ${d.provider_name}`),
              ),
              React.createElement("span", {
                style: {
                  padding:"3px 10px", borderRadius:"12px",
                  background: sty.bg, color: sty.color,
                  fontSize:"11px", fontWeight:600,
                },
              }, sty.label),
            ),

            // Row 2: attribution + timestamps. When a draft has been
            // manually edited (edited_at populated), surface that on
            // its own second line so the approver knows they're
            // looking at the attorney's version, not the auto-gen.
            React.createElement("div", {
              style: { fontSize:"11px", color:C.textMuted, marginBottom: canAction ? "10px" : 0 },
            },
              d.status === "draft" && `Generated by ${d.generated_by_name || "—"} · ${_relTime(d.generated_at)}`,
              d.status === "approved" && `Approved by ${d.approved_by_name || "—"} · ${_relTime(d.approved_at)}`,
              d.status === "rejected" && (
                `Rejected by ${d.rejected_by_name || "—"} · ${_relTime(d.rejected_at)}` +
                (d.rejection_reason ? ` · "${d.rejection_reason}"` : "")
              ),
              d.responsible_attorney_name && React.createElement("span", null,
                ` · Attorney of record: ${d.responsible_attorney_name}`,
              ),
              d.edited_at && React.createElement("div", {
                style: { color:C.gold, fontWeight:500, marginTop:"3px" },
              }, `✎ Manually edited by ${d.edited_by_name || "—"} · ${_relTime(d.edited_at)}`),
            ),

            // Row 3: action buttons — only for drafts in "draft" status.
            // Layout note: Preview + Download + Upload sit on the left
            // (review affordances); Approve + Reject sit on the right
            // (decision affordances). visualSpacer div pushes them apart.
            canAction && React.createElement("div", {
              style: { display:"flex", gap:"8px", flexWrap:"wrap", alignItems:"center" },
            },
              // Review affordances. Preview opens inline modal
              // instead of a new tab so the attorney doesn't lose
              // context of which draft they're reviewing.
              React.createElement("button", {
                onClick: () => setPreviewingDraft(d),
                style: {
                  padding:"5px 12px", borderRadius:"6px",
                  border:`1px solid ${C.warmGray}`,
                  background:"#fff", color:C.navy,
                  fontSize:"12px", fontWeight:500, cursor:"pointer",
                },
              }, "Preview PDF"),
              React.createElement("a", {
                href: `/api/drafts/${d.id}/download`,
                download: true,
                style: {
                  padding:"5px 12px", borderRadius:"6px",
                  border:`1px solid ${C.warmGray}`,
                  background:"#fff", color:C.navy,
                  fontSize:"12px", fontWeight:500, textDecoration:"none",
                },
              }, "Download .docx"),
              // Upload uses a hidden file input triggered by the label.
              // <label> wrapping <input type="file"> is the standard
              // "styled file picker" pattern without extra JS.
              React.createElement("label", {
                style: {
                  padding:"5px 12px", borderRadius:"6px",
                  border:`1px solid ${C.warmGray}`,
                  background:"#fff", color:C.navy,
                  fontSize:"12px", fontWeight:500,
                  cursor: actioningDraftId ? "wait" : "pointer",
                  display:"inline-block",
                  opacity: actioningDraftId ? 0.6 : 1,
                },
              },
                "Upload edited",
                React.createElement("input", {
                  type: "file",
                  accept: ".docx",
                  disabled: !!actioningDraftId,
                  onChange: e => {
                    const file = e.target.files && e.target.files[0];
                    if (file) handleUploadReplacement(d, file);
                    // Reset so re-uploading the same file re-triggers onChange
                    e.target.value = "";
                  },
                  style: { display:"none" },
                }),
              ),
              // Spacer pushes decision buttons to the right on wide
              // screens; wraps naturally on narrow ones
              React.createElement("div", { style: { flex: 1, minWidth: 0 } }),
              // Decision affordances
              React.createElement("button", {
                onClick: () => handleApprove(d),
                disabled: isActioning || !!actioningDraftId,
                style: {
                  padding:"5px 14px", borderRadius:"6px",
                  border:`1px solid ${C.success}`,
                  background: isActioning ? C.warmWhite : C.success,
                  color: isActioning ? C.textMuted : "#fff",
                  fontSize:"12px", fontWeight:600,
                  cursor: actioningDraftId ? "wait" : "pointer",
                },
              }, isActioning ? "Uploading…" : "Approve → Clio"),
              React.createElement("button", {
                onClick: () => handleReject(d),
                disabled: isActioning || !!actioningDraftId,
                style: {
                  padding:"5px 12px", borderRadius:"6px",
                  border:`1px solid ${C.warmGray}`,
                  background:"#fff", color:C.error,
                  fontSize:"12px", fontWeight:500,
                  cursor: actioningDraftId ? "wait" : "pointer",
                },
              }, "Reject"),
            ),

            // For approved drafts, show Clio doc link if we have it
            d.status === "approved" && d.clio_document_id && React.createElement("div", {
              style: { fontSize:"11px", color:C.textLight, marginTop:"4px" },
            }, `Clio doc: ${d.clio_document_id}`),

            // Substep 5-ν.3: send an approved draft out. Only offered
            // once approved — an unreviewed draft shouldn't be
            // faxable, since a records request is irreversible once
            // it lands on someone's machine.
            d.status === "approved" && React.createElement("div", {
              style: { marginTop:"8px", paddingTop:"8px",
                       borderTop:`1px solid ${C.warmGray}60`,
                       display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap" },
            },
              React.createElement("button", {
                onClick: () => openSend(d),
                style: {
                  padding:"5px 14px", borderRadius:"6px", border:"none",
                  background:C.navy, color:"#fff", fontSize:"12px",
                  fontWeight:600, cursor:"pointer",
                },
              }, "Send…"),
              React.createElement("span", {
                style: { fontSize:"10px", color:C.textMuted },
              }, "Fax or email this to a provider"),
            ),
          );
        }),
      ),
    ),
  ),  // ← closes the main card div

  // Substep 5-ν.3: send modal. Recipients come from the universal
  // directory so the delivery result feeds that contact's reliability.
  sendingDraft && React.createElement("div", {
    role:"dialog", "aria-modal":"true", "aria-label":"Send document",
    onClick: e => { if (e.target === e.currentTarget) setSendingDraft(null); },
    style: { position:"fixed", inset:0, background:"rgba(27,42,74,0.6)",
             display:"flex", alignItems:"center", justifyContent:"center",
             zIndex:1100, padding:"40px 20px" },
  },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"22px",
               maxWidth:"620px", width:"100%", maxHeight:"86vh",
               display:"flex", flexDirection:"column" },
    },
      React.createElement("h3", {
        style: { fontFamily:"'Playfair Display',serif", fontSize:"19px",
                 color:C.navy, margin:"0 0 3px 0" },
      }, "Send document"),
      React.createElement("div", {
        style: { fontSize:"12px", color:C.textMuted, marginBottom:"14px" },
      }, sendingDraft.template_filename),

      React.createElement("div", { style: { display:"flex", gap:"8px", marginBottom:"10px" } },
        React.createElement("select", {
          value: sendChannel, onChange: e => setSendChannel(e.target.value),
          style: { padding:"8px 10px", borderRadius:"6px",
                   border:`1px solid ${C.warmGray}`, fontSize:"13px", background:"#fff" },
        },
          React.createElement("option", { value:"fax" }, "Fax"),
          React.createElement("option", { value:"email" }, "Email"),
        ),
        React.createElement("input", {
          type:"text", value: sendSearch,
          onChange: e => setSendSearch(e.target.value),
          placeholder:"Filter recipients…",
          style: { flex:1, padding:"8px 12px", borderRadius:"6px",
                   border:`1px solid ${C.warmGray}`, fontSize:"13px" },
        }),
      ),

      React.createElement("div", {
        style: { flex:1, overflowY:"auto", border:`1px solid ${C.warmGray}`,
                 borderRadius:"8px", marginBottom:"12px", minHeight:"180px" },
      },
        sendRecipients
          .filter(r => !sendSearch ||
            `${r.name} ${r.facility || ""} ${r.department || ""}`
              .toLowerCase().includes(sendSearch.toLowerCase()))
          .map((r, i) => {
            const dest = sendChannel === "fax" ? r.fax : r.email;
            const wrongChannel = sendChannel === "fax" && r.submission_method
              && r.submission_method !== "fax";
            const selectable = !!dest && !wrongChannel;
            return React.createElement("div", {
              key: r.id,
              onClick: () => selectable && setSendChoice(r),
              style: {
                padding:"9px 12px",
                borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
                background: sendChoice?.id === r.id ? `${C.gold}18` : "#fff",
                cursor: selectable ? "pointer" : "not-allowed",
                opacity: selectable ? 1 : 0.55,
              },
            },
              React.createElement("div", {
                style: { display:"flex", alignItems:"center", gap:"7px", flexWrap:"wrap" },
              },
                React.createElement("span", {
                  style: { fontSize:"13px", fontWeight:600, color:C.navy },
                }, r.name),
                r.department && React.createElement("span", {
                  style: { fontSize:"10px", padding:"1px 7px", borderRadius:"999px",
                           background:`${C.navy}10`, color:C.navy, fontWeight:600 },
                }, r.department),
                r.is_flagged && React.createElement("span", {
                  style: { fontSize:"10px", padding:"1px 7px", borderRadius:"999px",
                           background:C.errorBg, color:C.error, fontWeight:700 },
                  title: r.flagged_reason,
                }, "FLAGGED"),
              ),
              React.createElement("div", {
                style: { fontSize:"11px", color: selectable ? C.textMuted : C.error, marginTop:"2px" },
              },
                wrongChannel
                  ? `Does not accept faxes — submit via ${r.submission_method}`
                  : (dest || `No ${sendChannel} on file`),
              ),
            );
          }),
        sendRecipients.length === 0 && React.createElement("div", {
          style: { padding:"28px", textAlign:"center", color:C.textMuted, fontSize:"12px" },
        }, "No recipients found."),
      ),

      React.createElement("div", {
        style: { display:"flex", justifyContent:"space-between", alignItems:"center", gap:"8px" },
      },
        React.createElement("span", { style: { fontSize:"10px", color:C.textMuted } },
          "Sends are simulated until a fax vendor is connected."),
        React.createElement("div", { style: { display:"flex", gap:"8px" } },
          React.createElement("button", {
            onClick: () => setSendingDraft(null), disabled: sending,
            style: { padding:"8px 16px", borderRadius:"6px",
                     border:`1px solid ${C.warmGray}`, background:"#fff",
                     color:C.textMuted, fontSize:"13px", cursor:"pointer" },
          }, "Cancel"),
          React.createElement("button", {
            onClick: doSend, disabled: sending || !sendChoice,
            style: { padding:"8px 20px", borderRadius:"6px", border:"none",
                     background: (sending || !sendChoice) ? C.warmGray : C.gold,
                     color:"#fff", fontSize:"13px", fontWeight:600,
                     cursor: (sending || !sendChoice) ? "default" : "pointer" },
          }, sending ? "Sending…" : `Send ${sendChannel}`),
        ),
      ),
    ),
  ),

  // Preview modal — rendered as sibling of the card so it overlays
  // the whole matter detail page, not just the card. Only mounted
  // when a draft is being previewed (previewingDraft non-null).
  previewingDraft && React.createElement(DraftPreviewModal, {
    draft: previewingDraft,
    canApprove: previewingDraft.status === "draft",
    isActioning: actioningDraftId === previewingDraft.id,
    onClose: () => setPreviewingDraft(null),
    onApprove: async () => {
      await handleApprove(previewingDraft);
      // handleApprove already updates drafts state. Close modal since
      // approved drafts don't need review anymore.
      setPreviewingDraft(null);
    },
    onReject: async () => {
      await handleReject(previewingDraft);
      setPreviewingDraft(null);
    },
    onRegenerate: async (overrides, writeBack) => {
      // Called from the field editor's Save button. Regenerates the
      // draft with edited merge-field values; the PDF preview refreshes
      // automatically because the iframe src is cache-busted via the
      // draft's updated_at (see DraftPreviewModal implementation).
      setActioningDraftId(previewingDraft.id);
      try {
        const resp = await apiFetch(`/api/drafts/${previewingDraft.id}/regenerate`, {
          method: "POST",
          body: JSON.stringify({
            field_overrides: overrides,
            write_back_to_clio: writeBack,
          }),
        });
        const updated = resp.data;
        setDrafts(prev => prev.map(d => d.id === updated.id ? updated : d));
        setPreviewingDraft(updated);
        showNotification(
          `Regenerated with ${Object.keys(overrides).length} field edit${Object.keys(overrides).length === 1 ? "" : "s"}.`,
          "success",
        );
      } catch (e) {
        showNotification(`Regenerate failed: ${e.message}`, "error");
      }
      setActioningDraftId(null);
    },
  }),
  );  // ← closes the Fragment
}


// ─────────────────────────────────────────────────────────────────────
// Field categorization for the edit-fields view. Keyed off the merge
// field names produced by ClioClient.gather_matter_data() — every
// draft's fields_used snapshot uses these same keys.
// ─────────────────────────────────────────────────────────────────────

const FIELD_CATEGORIES = [
  { label: "Client", match: (k) => k.startsWith("client_") },
  { label: "Attorney", match: (k) => k.startsWith("attorney_") },
  { label: "Provider", match: (k) => k.startsWith("provider_") },
  { label: "At-Fault Carrier", match: (k) =>
      k.startsWith("insurance_") || k.startsWith("insurer_") ||
      k.startsWith("adjuster_") || k === "claim_number" ||
      k === "defendant_name" || k === "insured_name" || k === "defendant_attorney" },
  { label: "UM / Med Pay", match: (k) => k.startsWith("um_") },
  { label: "Matter", match: (k) =>
      k === "matter_number" || k === "matter_status" || k === "matter_description" ||
      k === "date_of_incident" || k === "date_of_loss" ||
      k === "location_of_loss" || k === "location_of_incident" || k === "incident_location" ||
      k === "incident_type" || k === "case_number" || k === "court_name" ||
      k === "sol_date" || k === "statute_of_limitations_date" || k === "policy_number" ||
      k === "client_injuries" || k === "ssn" || k === "today_date" || k === "state" },
  { label: "Firm", match: (k) => k.startsWith("firm_") },
];

function _categorizeFields(fields) {
  const buckets = {};
  for (const cat of FIELD_CATEGORIES) buckets[cat.label] = [];
  buckets["Other"] = [];
  if (!fields) return buckets;

  for (const [key, val] of Object.entries(fields)) {
    // Skip empty values — no reason to render an input for a field
    // that has no current value (attorney can add it via the
    // missing-fields modal on generate if it's actually required)
    if (val === null || val === undefined || val === "") continue;
    // Skip non-scalar (shouldn't occur since backend filters, but
    // defensive)
    if (typeof val === "object") continue;

    const cat = FIELD_CATEGORIES.find((c) => c.match(key));
    buckets[cat ? cat.label : "Other"].push({ key, value: String(val) });
  }
  for (const key in buckets) {
    buckets[key].sort((a, b) => a.key.localeCompare(b.key));
  }
  return buckets;
}

// Modal that shows the draft's PDF preview inline with approve/reject
// buttons docked below. Also supports an "Edit fields" mode that
// swaps the PDF viewer for a categorized field editor. Saving from
// the editor regenerates the draft from the template with the edited
// values (zero formatting risk — the template file itself isn't
// touched).
//
// <iframe> is the widely-supported way to embed a PDF; browsers
// fall back to their built-in PDF viewer.
//
// Escape key closes the modal; click-on-backdrop closes too (but
// clicks inside the modal don't propagate).
function DraftPreviewModal({ draft, canApprove, isActioning, onClose, onApprove, onReject, onRegenerate }) {
  const [mode, setMode] = useState("preview"); // "preview" | "edit"
  const [overrides, setOverrides] = useState({});
  const [writeBack, setWriteBack] = useState(true);
  const [fieldSearch, setFieldSearch] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Re-categorize whenever the draft's fields_used changes (after a
  // regenerate the modal receives the updated draft object)
  const categorized = useMemo(() => _categorizeFields(draft.fields_used || {}), [draft.fields_used]);

  // Cache-bust the PDF iframe when updated_at changes so the newly-
  // regenerated preview appears immediately without a browser refresh
  const pdfSrc = `/api/drafts/${draft.id}/preview.pdf?v=${encodeURIComponent(draft.updated_at || draft.generated_at || "")}`;

  const handleSaveEdits = async () => {
    if (Object.keys(overrides).length === 0) {
      // Nothing changed — just switch back to preview
      setMode("preview");
      return;
    }
    await onRegenerate(overrides, writeBack);
    // Clear the collected overrides + return to preview view. Draft
    // prop refreshed by parent already.
    setOverrides({});
    setMode("preview");
  };

  const handleCancelEdits = () => {
    setOverrides({});
    setFieldSearch("");
    setMode("preview");
  };

  return React.createElement("div", {
    onClick: onClose,
    style: {
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(0,0,0,0.55)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"32px",
    },
  },
    React.createElement("div", {
      onClick: e => e.stopPropagation(),
      style: {
        background:"#fff", borderRadius:"12px",
        width:"100%", maxWidth:"1000px", height:"100%",
        display:"flex", flexDirection:"column",
        overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.35)",
      },
    },
      // Header bar
      React.createElement("div", {
        style: {
          padding:"14px 20px", borderBottom:`1px solid ${C.warmGray}`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          background: C.warmWhite,
        },
      },
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy } },
            draft.template_filename,
            draft.provider_name && React.createElement("span", {
              style: { color:C.textMuted, fontWeight:400 },
            }, ` · ${draft.provider_name}`),
            mode === "edit" && React.createElement("span", {
              style: { color:C.gold, fontWeight:600, marginLeft:"10px" },
            }, "· Editing fields"),
          ),
          React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, marginTop:"2px" } },
            draft.edited_at
              ? `✎ Attorney-edited version · ${_relTime(draft.edited_at)}`
              : `Auto-generated draft · ${_relTime(draft.generated_at)}`,
          ),
        ),
        React.createElement("button", {
          onClick: onClose,
          title: "Close (Esc)",
          style: {
            border:"none", background:"none", cursor:"pointer",
            fontSize:"22px", color:C.textLight, padding:"0 6px",
          },
        }, "×"),
      ),

      // Body: PDF preview OR field editor
      mode === "preview"
        ? React.createElement("iframe", {
            src: pdfSrc,
            title: `Preview of ${draft.template_filename}`,
            style: { flex:1, width:"100%", border:"none", background:"#525659" },
          })
        : _renderFieldsEditor({
            categorized, overrides, setOverrides,
            fieldSearch, setFieldSearch,
            writeBack, setWriteBack,
            isActioning,
          }),

      // Footer bar — different actions depending on mode
      React.createElement("div", {
        style: {
          padding:"12px 20px", borderTop:`1px solid ${C.warmGray}`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          background:"#fff", gap:"12px", flexWrap:"wrap",
        },
      },
        mode === "preview"
          ? React.createElement(React.Fragment, null,
              React.createElement("a", {
                href: `/api/drafts/${draft.id}/download`,
                download: true,
                style: {
                  padding:"6px 14px", borderRadius:"6px",
                  border:`1px solid ${C.warmGray}`,
                  background:"#fff", color:C.navy,
                  fontSize:"12px", fontWeight:500, textDecoration:"none",
                },
              }, "Download .docx"),

              canApprove && React.createElement("div", {
                style: { display:"flex", gap:"8px", alignItems:"center" },
              },
                React.createElement("button", {
                  onClick: () => setMode("edit"),
                  disabled: isActioning,
                  style: {
                    padding:"6px 14px", borderRadius:"6px",
                    border:`1px solid ${C.gold}`, background:"#fff",
                    color:C.gold, fontSize:"12px", fontWeight:600,
                    cursor: isActioning ? "wait" : "pointer",
                  },
                }, "Edit fields"),
                React.createElement("button", {
                  onClick: onReject,
                  disabled: isActioning,
                  style: {
                    padding:"6px 14px", borderRadius:"6px",
                    border:`1px solid ${C.warmGray}`, background:"#fff",
                    color:C.error, fontSize:"12px", fontWeight:500,
                    cursor: isActioning ? "wait" : "pointer",
                  },
                }, "Reject"),
                React.createElement("button", {
                  onClick: onApprove,
                  disabled: isActioning,
                  style: {
                    padding:"6px 16px", borderRadius:"6px",
                    border:`1px solid ${C.success}`,
                    background: isActioning ? C.warmWhite : C.success,
                    color: isActioning ? C.textMuted : "#fff",
                    fontSize:"12px", fontWeight:600,
                    cursor: isActioning ? "wait" : "pointer",
                  },
                }, isActioning ? "Uploading…" : "Approve → Clio"),
              ),
            )
          : React.createElement(React.Fragment, null,
              React.createElement("div", {
                style: { fontSize:"12px", color:C.textMuted },
              },
                Object.keys(overrides).length > 0
                  ? `${Object.keys(overrides).length} field${Object.keys(overrides).length === 1 ? "" : "s"} edited`
                  : "No changes yet",
              ),
              React.createElement("div", { style: { display:"flex", gap:"8px", alignItems:"center" } },
                React.createElement("button", {
                  onClick: handleCancelEdits,
                  disabled: isActioning,
                  style: {
                    padding:"6px 14px", borderRadius:"6px",
                    border:`1px solid ${C.warmGray}`, background:"#fff",
                    color:C.textMuted, fontSize:"12px", fontWeight:500,
                    cursor: isActioning ? "wait" : "pointer",
                  },
                }, "Cancel"),
                React.createElement("button", {
                  onClick: handleSaveEdits,
                  disabled: isActioning,
                  style: {
                    padding:"6px 16px", borderRadius:"6px",
                    border:`1px solid ${C.gold}`,
                    background: isActioning ? C.warmWhite : C.gold,
                    color: isActioning ? C.textMuted : "#fff",
                    fontSize:"12px", fontWeight:600,
                    cursor: isActioning ? "wait" : "pointer",
                  },
                }, isActioning ? "Regenerating…" : "Save & regenerate"),
              ),
            ),
      ),
    ),
  );
}

// Fields editor body — categorized inputs, one per merge field.
// Search box at top filters across categories. Save button lives in
// the modal footer, not here.
function _renderFieldsEditor({
  categorized, overrides, setOverrides,
  fieldSearch, setFieldSearch,
  writeBack, setWriteBack,
  isActioning,
}) {
  const search = (fieldSearch || "").toLowerCase().trim();

  const setField = (key, value) => {
    setOverrides(prev => ({ ...prev, [key]: value }));
  };
  const revertField = (key) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Category order — start with the most-often-edited, push Other last
  const orderedCats = [...FIELD_CATEGORIES.map(c => c.label), "Other"];

  return React.createElement("div", {
    style: {
      flex:1, overflowY:"auto", padding:"18px 22px",
      background:"#fafafa",
    },
  },
    // Instructional strip
    React.createElement("div", {
      style: {
        fontSize:"12px", color:C.textMuted, marginBottom:"12px",
        padding:"10px 12px", borderRadius:"8px",
        background:"#fff", border:`1px solid ${C.warmGray}40`,
      },
    },
      "Edit any field below and click ",
      React.createElement("strong", { style: { color:C.navy } }, "Save & regenerate"),
      ". The template's formatting (letterhead, tables, signature blocks) is preserved exactly — only the values change.",
    ),

    // Search filter
    React.createElement("input", {
      type: "text",
      placeholder: "Search fields (e.g. client, provider, insurance)…",
      value: fieldSearch,
      onChange: e => setFieldSearch(e.target.value),
      disabled: isActioning,
      style: {
        width:"100%", padding:"8px 12px", marginBottom:"14px",
        borderRadius:"6px", border:`1px solid ${C.warmGray}`,
        fontSize:"13px", background:"#fff", color:C.textDark,
      },
    }),

    // Categorized field sections
    orderedCats.map(catLabel => {
      const items = (categorized[catLabel] || []).filter(item =>
        !search || item.key.toLowerCase().includes(search) || (item.value || "").toLowerCase().includes(search)
      );
      if (items.length === 0) return null;

      return React.createElement("div", {
        key: catLabel,
        style: { marginBottom:"18px" },
      },
        React.createElement("div", {
          style: {
            fontSize:"11px", fontWeight:700, color:C.textMuted,
            textTransform:"uppercase", letterSpacing:"0.06em",
            marginBottom:"8px", paddingLeft:"2px",
          },
        }, catLabel),
        React.createElement("div", { style: { display:"flex", flexDirection:"column", gap:"6px" } },
          items.map(item => {
            const isEdited = overrides.hasOwnProperty(item.key);
            const currentValue = isEdited ? overrides[item.key] : item.value;
            return React.createElement("div", {
              key: item.key,
              style: {
                display:"grid", gridTemplateColumns:"200px 1fr auto",
                gap:"10px", alignItems:"center",
                padding:"6px 10px", borderRadius:"6px",
                background: isEdited ? `${C.gold}12` : "#fff",
                border: `1px solid ${isEdited ? C.gold + "60" : C.warmGray + "40"}`,
              },
            },
              React.createElement("label", {
                htmlFor: `fld-${item.key}`,
                title: item.key,
                style: {
                  fontSize:"11px", fontWeight:600, color:C.textDark,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                },
              }, _humanizeFieldKey(item.key)),
              React.createElement("input", {
                id: `fld-${item.key}`,
                type: item.key.includes("date") ? "date" : "text",
                value: currentValue,
                onChange: e => setField(item.key, e.target.value),
                disabled: isActioning,
                style: {
                  width:"100%", padding:"6px 8px", borderRadius:"4px",
                  border:`1px solid ${C.warmGray}`, background:"#fff",
                  fontSize:"12px", color:C.textDark,
                },
              }),
              isEdited && React.createElement("button", {
                onClick: () => revertField(item.key),
                disabled: isActioning,
                title: `Revert to "${item.value}"`,
                style: {
                  padding:"2px 8px", borderRadius:"4px",
                  border:`1px solid ${C.warmGray}`, background:"#fff",
                  color:C.textMuted, fontSize:"11px", cursor:"pointer",
                },
              }, "↺ Revert"),
              !isEdited && React.createElement("span", null),
            );
          }),
        ),
      );
    }).filter(Boolean),

    // Empty state — search returned nothing
    orderedCats.every(cat => {
      const items = (categorized[cat] || []).filter(item =>
        !search || item.key.toLowerCase().includes(search) || (item.value || "").toLowerCase().includes(search)
      );
      return items.length === 0;
    }) && React.createElement("div", {
      style: { padding:"30px", textAlign:"center", color:C.textMuted, fontSize:"13px" },
    }, search ? `No fields match "${fieldSearch}"` : "No editable fields on this draft."),

    // Write-back toggle
    React.createElement("label", {
      style: {
        display:"flex", alignItems:"center", gap:"8px",
        padding:"10px 12px", marginTop:"8px",
        borderRadius:"6px",
        background: writeBack ? `${C.gold}10` : "#fff",
        border: `1px solid ${writeBack ? C.gold + "40" : C.warmGray}`,
        cursor:"pointer", fontSize:"12px", color:C.textDark,
      },
    },
      React.createElement("input", {
        type: "checkbox",
        checked: writeBack,
        onChange: () => setWriteBack(!writeBack),
        disabled: isActioning,
        style: { cursor:"pointer" },
      }),
      React.createElement("span", null,
        "Save these values to Clio's matter custom fields (writable fields only). ",
        React.createElement("span", { style: { color:C.textMuted } },
          "So the next generation of any template using these fields uses your edits.",
        ),
      ),
    ),
  );
}

// "client_first_name" → "Client First Name". Rough humanization for
// merge-field keys. Falls back to the key itself if it's not obviously
// snake_case.
function _humanizeFieldKey(key) {
  if (!key) return "";
  return key
    .split("_")
    .map(w => w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w)
    .join(" ");
}

// Relative-time formatter kept local — small, no need to extract.
// Falls back to raw ISO if parsing fails.
function _relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - t);
    if (diff < 60_000) return "just now";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min ago`;
    if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
